const sectionStyle = {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "60px 20px"
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "20px"
};

const cardStyle = {
  border: "1px solid #eee",
  borderRadius: "12px",
  padding: "20px",
  background: "#fff",
  boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
  transition: "all 0.2s ease"
};

const hoverEffect = (e, enter = true) => {
  e.currentTarget.style.boxShadow = enter
    ? "0 15px 35px rgba(0,0,0,0.1)"
    : "0 10px 25px rgba(0,0,0,0.05)";
  e.currentTarget.style.transform = enter
    ? "translateY(-3px)"
    : "translateY(0)";
};

const ServerlessCandidateSystem = () => {
  return (
    <div>

      {/* HERO */}
      <div style={{ ...sectionStyle, paddingTop: "80px" }}>
        <h1 style={{ fontSize: "36px", marginBottom: "20px" }}>
          Serverless Candidate Processing System on AWS
        </h1>

        <p style={{ fontSize: "18px", color: "#555", maxWidth: "700px" }}>
          Designing a scalable, event-driven system for automated document processing,
          data extraction, and workflow orchestration using AWS serverless services.
        </p>
      </div>


      {/* IMAGE */}
      <div style={{ ...sectionStyle, paddingTop: "0", textAlign: "center" }}>
        <img
          src="/images/recruitmentAgencyDiagram.png"
          alt="Serverless AWS Architecture"
          style={{
            width: "100%",
            maxWidth: "750px",
            borderRadius: "14px",
            boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
          }}
        />

        <div style={{ fontSize: "14px", color: "#777", marginTop: "10px" }}>
          Example serverless architecture used in real-world projects
        </div>
      </div>


      {/* CONTEXT */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          Processing candidate documents efficiently is a common challenge in recruitment and
          document-driven workflows. Traditional approaches often rely on manual processing or
          tightly coupled systems, limiting scalability and automation.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          This project demonstrates how to design a fully serverless system that automates
          document processing while maintaining scalability, reliability, and cost efficiency.
        </p>
      </div>


      {/* CHALLENGE */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>Problem to Solve</h2>

        <div style={gridStyle}>
          {[
            "Handling document uploads at scale (CVs, applications)",
            "Extracting structured data from unstructured documents",
            "Storing and managing candidate data efficiently",
            "Triggering automated workflows and notifications",
            "Scaling without managing infrastructure"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              ✓ {item}
            </div>
          ))}
        </div>
      </div>


      {/* ARCHITECTURE */}
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: "25px" }}>Architecture Approach</h2>

        <p style={{ marginBottom: "25px", lineHeight: "1.7" }}>
          The system is designed using a decoupled, event-driven architecture where each component
          is responsible for a specific function, enabling independent scaling and high reliability.
        </p>

        <div style={gridStyle}>
          {[
            "Amazon S3 for document ingestion and storage",
            "AWS Lambda for processing and orchestration logic",
            "Amazon Textract for extracting structured data",
            "Amazon DynamoDB for storing candidate information",
            "Amazon SNS / SQS for asynchronous communication",
            "AWS Step Functions for workflow orchestration"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              ✓ {item}
            </div>
          ))}
        </div>
      </div>


      {/* CAPABILITIES */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>
          What This System Enables
        </h2>

        <div style={gridStyle}>
          {[
            "Fully automated document processing pipelines",
            "Scalable handling of unpredictable workloads",
            "Decoupled, maintainable system components",
            "Event-driven processing and orchestration",
            "Integration of AI services into workflows",
            "Cost-efficient pay-per-use execution model"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* BUSINESS VALUE 🔥 */}
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: "25px" }}>
          Business Impact
        </h2>

        <div style={gridStyle}>
          {[
            "Reduced manual work and processing time",
            "Improved consistency and data accuracy",
            "Lower operational costs (no infrastructure management)",
            "Faster onboarding and decision-making processes",
            "Scalable solution adaptable to growing workloads"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* TAKEAWAY */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          This system demonstrates how serverless architecture combined with AI services
          enables building scalable and efficient solutions without managing infrastructure.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          The same architectural patterns can be applied across multiple domains, including
          document processing, data pipelines, and automation workflows.
        </p>
      </div>


      {/* CTA 🔥 */}
      <div style={{
        textAlign: "center",
        margin: "80px 0",
        padding: "50px 20px",
        background: "#fafafa"
      }}>
        <h2 style={{ marginBottom: "15px" }}>
          Build a Similar System for Your Business
        </h2>

        <p style={{ color: "#555", marginBottom: "25px" }}>
          Let’s design a scalable, automated architecture tailored to your needs.
        </p>

        <a
          href="/contact"
          style={{
            display: "inline-block",
            padding: "14px 32px",
            background: "#007bff",
            color: "#fff",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: "500",
            boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
          }}
        >
          Discuss Your Use Case →
        </a>
      </div>

    </div>
  );
};

export default ServerlessCandidateSystem;